import React from 'react';
import moment from 'moment';
import { CustomDatePicker } from '../../../components/DateTimePicker';
import { CustomTextField } from '../../../components/Textfield';
import { FileUploader } from '../../../components/UI';
import { showMessage } from '../../../utils/helper';
import {
    FieldRow,
    RejectReasonRow,
    formatDob,
    verifyKYCStyles,
} from './verifyKYCHeplers';
import { Box, Grid, Tooltip, Typography } from '@material-ui/core';
import { LinkOffOutlined, LinkOutlined } from '@material-ui/icons';
import DoneAllOutlinedIcon from '@material-ui/icons/DoneAllOutlined';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import CancelPresentationOutlinedIcon from '@material-ui/icons/CancelPresentationOutlined';
import {
    CheckMarkGreen,
    CrossMarkIcon,
    ExclaimIcon,
} from '../../../assets/svg';
import FileUploaderTyped from '../../../components/UI/FileUploader/FileUploaderTyped';
import {
    KycImageRevealRow,
    useRevealedImage,
} from '../../../components/KYC/reveal';
import { getMaxDOBFor18Years, isImageLink } from '../../../utils/appHelper';
import { isDigilockerVerified } from '../../../utils/appHelper';
import { PdfViewerButton } from '../../../components/Modals/PdfViewerModalV2';

interface DLStateCardProps {
    payload: any;
    setPayload: Function;
}

const DLStateCard: React.FC<DLStateCardProps> = ({ payload, setPayload }) => {
    // A live UNBLUR_IMAGE grant swaps in the raw object; otherwise this is whatever
    // the API served (the blurred variant, or nothing while masking is pending).
    const revealedFrontImage = useRevealedImage(
        payload?.id,
        payload?.imageFront,
        'FRONT',
    );
    const revealedBackImage = useRevealedImage(
        payload?.id,
        payload?.imageBack,
        'BACK',
    );
    const styles = verifyKYCStyles({});
    const isDocumentIdCreated = !!payload?.id;
    const isManualVerificationRequired = payload?.manualVerificationRequired;
    const isDLAutoVerified = !!payload && payload?.dlCaseCheck === 'VERIFIED';
    const isApproved =
        !!isDLAutoVerified || payload?.approvalStatus === 'APPROVED';
    const isDLFailed = payload?.dlCaseCheck === 'INITIATION_FAILED';
    let isVerifiedByDigiLocker = isDigilockerVerified(payload);

    const isRejected = !!isDLFailed || payload?.approvalStatus === 'REJECTED';
    const isPending = !isApproved && !isRejected;
    const isDocumentNumberMatching = !!payload?.autoKycDocumentNo
        ? !!payload?.isDocumentNumberMatching
        : true;
    const isDLDobMatching = true; //!!payload?.isDobMatching;
    const enableUpdate =
        !isDocumentIdCreated ||
        !payload?.dlCaseCheck ||
        payload?.dlCaseCheck == null ||
        !!isDLFailed
            ? true
            : false;
    console.log(
        'DLStateCard payload:',
        isManualVerificationRequired,
        enableUpdate,
        payload,
    );
    const isError =
        isManualVerificationRequired &&
        isPending &&
        (!isDocumentNumberMatching || !isDLDobMatching);

    const statusText = !!isVerifiedByDigiLocker
        ? 'Verified by Digilocker'
        : !isDocumentIdCreated
          ? ''
          : isApproved
            ? 'Verified'
            : isRejected
              ? 'Initiation Failed'
              : isManualVerificationRequired &&
                  (!isDocumentNumberMatching || !isDLDobMatching)
                ? 'Manual Verification Required'
                : '';

    const statusColor = !isDocumentIdCreated
        ? 'orange'
        : isApproved
          ? '#4caf50'
          : isRejected
            ? '#f44336'
            : isManualVerificationRequired &&
                (!isDocumentNumberMatching || !isDLDobMatching)
              ? '#f44336'
              : '';

    const scannedId = payload?.autoKycDocumentNo;
    const enteredId = payload?.documentNo ?? '';
    const scannedDobRaw = payload?.autoKycDocumentDob;
    const enteredDobRaw = payload?.docDate;
    const isOtherDocumentFound = !!payload?.pdfDocumentLink;

    return (
        <Grid
            item
            xs={6}
            className={
                isError
                    ? styles.gridItemContainerWithErrorBorder
                    : styles.gridItemContainer
            }
        >
            <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box display="flex" alignItems="center">
                    <Typography className={styles.title} variant="h6">
                        Driving License Details
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography
                        style={{
                            fontWeight: 700,
                            marginLeft: 5,
                            color: statusColor,
                            fontSize: 14,
                        }}
                        variant="h6"
                        component="span"
                        gutterBottom
                    >
                        {statusText}
                    </Typography>
                    <Box ml="0.2rem">
                        {isApproved ? (
                            <CheckMarkGreen style={{ width: '16px' }} />
                        ) : isRejected ? (
                            <CrossMarkIcon style={{ width: '16px' }} />
                        ) : null}
                    </Box>
                </Box>
            </Box>

            {isRejected && !!payload?.rejectReason && (
                <Box style={{ width: '100%', marginTop: 8 }}>
                    <RejectReasonRow reason={payload?.rejectReason} />
                </Box>
            )}

            {/* Body: show different layout if manual verification with mismatch vs normal view */}
            <Box style={{ width: '100%', marginTop: 8 }}>
                {/* ID row */}
                {!!isDocumentIdCreated && (
                    <FieldRow
                        label={scannedId ? 'Scanned Id:' : 'Entered Id:'}
                        scannedValue={scannedId}
                        enteredValue={enteredId}
                        showEdited={
                            !!scannedId &&
                            !!enteredId &&
                            !!payload?.docNumberMismatch
                        }
                        showWarning={!isDocumentNumberMatching}
                        documentId={payload?.id}
                        documentType={'DRIVING_LICENSE'}
                        revealable={payload?.revealable}
                        revealSubject={'DL number'}
                    />
                )}

                {/* DOB row */}
                {!!isDocumentIdCreated && (
                    <FieldRow
                        label={scannedDobRaw ? 'Scanned DOB:' : 'Entered DOB:'}
                        scannedValue={
                            scannedDobRaw ? formatDob(scannedDobRaw) : undefined
                        }
                        enteredValue={
                            enteredDobRaw ? formatDob(enteredDobRaw) : undefined
                        }
                        showEdited={
                            !!scannedDobRaw &&
                            !!enteredDobRaw &&
                            formatDob(scannedDobRaw) !==
                                formatDob(enteredDobRaw)
                        }
                        showWarning={!isDLDobMatching}
                    />
                )}
                {!!isOtherDocumentFound && (
                    <PdfViewerButton
                        file={payload?.pdfDocumentLink}
                        variant="link"
                        linkText="Click to view PDF document from DigiLocker"
                    />
                )}
                {/* If manual verification with mismatch show editable read-only field as before */}
                {enableUpdate ? (
                    <Box width={'100%'}>
                        <CustomTextField
                            value={enteredId}
                            onChange={(e: any) =>
                                setPayload({
                                    ...payload,
                                    documentNo: e?.target?.value?.trim(),
                                })
                            }
                            label="DL Number"
                            variant="outlined"
                            style={{
                                width: '100%',
                                marginBottom: 16,
                                marginTop: 8,
                            }}
                            InputProps={{
                                readOnly: !enableUpdate,
                                //endAdornment: <ExclaimIcon />,
                            }}
                        />
                        <CustomDatePicker
                            label="Date of Birth"
                            id="dob"
                            name="dob"
                            variant="outlined"
                            value={payload?.docDate ?? null}
                            disabled={!enableUpdate}
                            showTodayButton={false}
                            allowAge18={false}
                            maximumDate={getMaxDOBFor18Years()}
                            onChange={(e: any) => {
                                setPayload({
                                    ...payload,
                                    docDate:
                                        moment(e)?.valueOf() > 0
                                            ? moment(e)?.format('DD-MMM-YYYY')
                                            : null,
                                });
                            }}
                            showInput={true}
                            style={{
                                width: '100%',
                                marginBottom: 16,
                                marginTop: 8,
                            }}
                            InputProps={{
                                readOnly: !enableUpdate,
                            }}
                        />
                    </Box>
                ) : null}
            </Box>

            <KycImageRevealRow
                documentId={payload?.id}
                documentType={'DRIVING_LICENSE'}
                imageRevealable={payload?.imageRevealable}
                imageUnmasked={payload?.imageUnmasked}
                documentKeys={['FRONT', 'BACK']}
                subject={'Driving Licence image'}
            />

            {!isVerifiedByDigiLocker && (
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent={'space-between'}
                    width={'100%'}
                >
                    <Box width={'48%'}>
                        <FileUploaderTyped
                            labelStyles={{
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                            disabled={!enableUpdate}
                            defaultValue={
                                !!isImageLink(revealedFrontImage)
                                    ? revealedFrontImage
                                    : null
                            }
                            flowType="KYC"
                            placeholder={'Upload DL (Front)'}
                            onFileUploaded={(e: any) => {
                                setPayload({
                                    ...payload,
                                    imageFront: e,
                                });
                            }}
                            onRemoved={(e: any) => {
                                setPayload({
                                    ...payload,
                                    imageFront: null,
                                });
                            }}
                        />
                    </Box>
                    <Box width={'48%'}>
                        <FileUploaderTyped
                            labelStyles={{
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                            disabled={!enableUpdate}
                            defaultValue={
                                !!isImageLink(revealedBackImage)
                                    ? revealedBackImage
                                    : null
                            }
                            flowType="KYC"
                            placeholder={'Upload DL (Back)'}
                            onFileUploaded={(e: any) => {
                                setPayload({
                                    ...payload,
                                    imageBack: e,
                                });
                            }}
                            onRemoved={(e: any) => {
                                setPayload({
                                    ...payload,
                                    imageBack: null,
                                });
                            }}
                        />
                    </Box>
                </Box>
            )}
        </Grid>
    );
};

export default DLStateCard;
